import { UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { parseSectionItemId, SectionItemRef } from '@griply/utils';
import { ID } from 'shared/types/id';

type ItemBase = {
  id: ID;
};

type SectionBase<Item extends ItemBase> = {
  id: ID;
  items: Item[];
  // this means that it does not sort the items, just adds it to the bottom of the section
  isDroppableOnly?: boolean;
};

export const handleItemBetweenSectionsMovement = <
  Item extends ItemBase,
  Section extends SectionBase<Item>,
>(
  sections: Section[],
  active: SectionItemRef,
  overId: UniqueIdentifier,
  excludedSectionIds: ID[] = [],
): { sections: Section[]; targetSectionId?: ID } => {
  const activeSectionIndex = sections.findIndex(
    ({ id }) => id === active.sectionId,
  );
  const activeSection = sections[activeSectionIndex];
  const overRef = parseSectionItemId(overId);
  const overSectionId = overRef?.sectionId ?? overId.toString();
  const overSectionIndex = sections.findIndex(({ id }) => id === overSectionId);
  const overSection = sections[overSectionIndex];

  // don't do anything if no containers found or if within the same container. Movement inside the container is handled elsewhere
  if (!activeSection || !overSection || activeSection.id === overSection.id) {
    return { sections };
  }

  // if the over section is excluded, no changes should be made
  if (excludedSectionIds.includes(overSection.id)) {
    return { sections };
  }

  const activeItem = activeSection.items.find(
    (item) => item.id === active.itemId,
  );

  // if the activeItem is not found, no changes can be done
  if (!activeItem) {
    return { sections };
  }

  // filter out the active item of the active section
  activeSection.items = activeSection.items.filter(
    ({ id }) => id !== active.itemId,
  );

  // the target already lists this item under its own link — one row is enough
  if (overSection.items.some(({ id }) => id === active.itemId)) {
    return { sections: [...sections], targetSectionId: overSection.id };
  }

  const activeComesFromTop = activeSectionIndex < overSectionIndex;

  // based on the order of the sections, add the item to the bottom or to the top
  let newIndex = activeComesFromTop ? 0 : overSection.items.length;

  // if hovered over an item inside the section, find the item index
  if (overRef) {
    const overItemIndex = overSection.items.findIndex(
      ({ id }) => id === overRef.itemId,
    );
    newIndex = overItemIndex + (activeComesFromTop ? 0 : 1);
  }

  // add the active item to the overSection in the place it would be expected of.
  overSection.items = overSection.items.toSpliced(newIndex, 0, activeItem);

  // return a new array, as the sections have changed data
  return { sections: [...sections], targetSectionId: overSection.id };
};

export const handleItemWithinSectionMovement = <
  Item extends ItemBase,
  Section extends SectionBase<Item>,
>(
  sections: Section[],
  active: SectionItemRef,
  overId: UniqueIdentifier,
  excludedSectionIds: ID[] = [],
): { sections: Section[]; activeItem?: Item } => {
  // we only need the active section, as the items should be moving within the same section
  const activeSection = sections.find(({ id }) => id === active.sectionId);

  // if the section is excluded, no changes should be made
  if (activeSection && excludedSectionIds.includes(activeSection.id)) {
    return { sections };
  }

  const overRef = parseSectionItemId(overId);
  const activeIndex =
    activeSection?.items.findIndex(({ id }) => id === active.itemId) ?? -1;
  const overIndex =
    overRef?.sectionId === active.sectionId
      ? activeSection?.items.findIndex(({ id }) => id === overRef.itemId) ?? -1
      : -1;

  // no activeSection means invalid data. Same for no indices
  if (!activeSection || activeIndex < 0 || overIndex < 0) {
    return { sections };
  }

  const activeItem = activeSection.items[activeIndex];
  // move the active item to the location of the over item
  activeSection.items = arrayMove(activeSection.items, activeIndex, overIndex);

  // return a new array, as the sections have changed data
  return { sections: [...sections], activeItem };
};
